// Fetch and normalize bibliographic metadata from Crossref's free REST API.
//
// Browser-safe by design: Crossref sends permissive CORS headers, so the SPA
// calls it directly — no Edge Function, no API key. We use it two ways:
//   1. fetchBookChapters() — paste a book link/DOI/ISBN, get every chapter as a
//      ready-to-submit bibliography entry (the "import a whole volume" flow).
//   2. fetchSingleEntry() — paste one DOI, get a single entry to autofill the
//      manual submit form.
//
// Crossref quirk worth knowing: a book-chapter's own `container-title` is the
// *series* ("Contributions to Hermeneutics"), NOT the book title. So when we
// import a whole book we fetch the book record separately for the real title +
// editors and stamp those onto every chapter.

import { EMPTY_ENTRY } from './bibliography.js';

const CROSSREF_BASE = 'https://api.crossref.org';

// Crossref asks API users to identify themselves; a contact address keeps us in
// the faster, more reliable "polite" request pool.
const MAILTO = 'admin@hermeneuticscommons.org';

// ISO 639-1 → English language name. Matches the convention the existing
// dataset uses for language_display (see scripts/import.js, e.g. "en: English").
export const LANGUAGE_NAMES = {
  en: 'English',
  fr: 'French',
  de: 'German',
  es: 'Spanish',
  it: 'Italian',
  pt: 'Portuguese',
  nl: 'Dutch',
  la: 'Latin',
  el: 'Greek',
  grc: 'Ancient Greek',
  ru: 'Russian',
  pl: 'Polish',
  cs: 'Czech',
  hu: 'Hungarian',
  ro: 'Romanian',
  gl: 'Galician',
  ca: 'Catalan',
  sv: 'Swedish',
  da: 'Danish',
  no: 'Norwegian',
  nb: 'Norwegian',
  fi: 'Finnish',
  he: 'Hebrew',
  ar: 'Arabic',
  ja: 'Japanese',
  zh: 'Chinese',
  ko: 'Korean',
  tr: 'Turkish',
  uk: 'Ukrainian',
};

// Map a Crossref work type onto our four bibliography types.
const TYPE_MAP = {
  'journal-article': 'article',
  'book-chapter': 'chapter',
  'book-part': 'chapter',
  'book-section': 'chapter',
  book: 'book',
  monograph: 'book',
  'reference-book': 'book',
  'edited-book': 'book',
  'proceedings-article': 'conference',
};

// Crossref types we treat as importable chapters of a book.
const CHAPTER_TYPES = new Set(['book-chapter', 'book-part', 'book-section']);

// Crossref types that represent the volume itself (the edited book).
const BOOK_TYPES = new Set(['book', 'monograph', 'edited-book', 'reference-book']);

// Matches a DOI anywhere in a pasted string (a Springer link, a doi.org URL, or
// the bare DOI). The character class stops at the URL fragment/query, so
// ".../book/10.1007/978-3-032-18675-1#toc" yields just the DOI.
const DOI_RE = /10\.\d{4,9}\/[^\s"'<>#?&]+/;

// Pull a DOI or ISBN out of pasted text. Returns { kind, value } or null.
export function parseIdentifier(input) {
  const raw = (input ?? '').trim();
  if (!raw) return null;

  const doiMatch = raw.match(DOI_RE);
  if (doiMatch) {
    return { kind: 'doi', value: doiMatch[0].replace(/[).,;]+$/, '') };
  }

  const digits = raw.replace(/[^0-9Xx]/g, '');
  if (digits.length === 13 || digits.length === 10) {
    return { kind: 'isbn', value: digits };
  }
  return null;
}

function formatPerson(p) {
  if (!p) return '';
  if (p.family && p.given) return `${p.family}, ${p.given}`;
  if (p.family) return p.family;
  if (p.name) return p.name; // organizations / institutional authors
  return '';
}

function formatPeople(arr) {
  return (arr ?? []).map(formatPerson).filter(Boolean);
}

function extractYear(item) {
  const parts =
    item?.published?.['date-parts'] ||
    item?.['published-print']?.['date-parts'] ||
    item?.['published-online']?.['date-parts'] ||
    item?.issued?.['date-parts'];
  const y = parts?.[0]?.[0];
  return typeof y === 'number' ? y : null;
}

// Crossref abstracts arrive as JATS XML; strip tags down to plain text.
function cleanAbstract(s) {
  if (!s) return '';
  return s
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function languageFields(code) {
  if (!code) return { language: '', language_display: '' };
  const lc = String(code).toLowerCase();
  return { language: lc, language_display: LANGUAGE_NAMES[lc] ?? '' };
}

// Map a single Crossref work to our bibliography form shape. `overrides` lets
// the whole-book importer stamp the real book title / editors onto a chapter.
export function crossrefToEntry(item, overrides = {}) {
  const type = TYPE_MAP[item.type] ?? 'article';
  const containerTitle = item['container-title']?.[0] ?? '';
  const { language, language_display } = languageFields(item.language);

  const entry = {
    ...EMPTY_ENTRY,
    type,
    title: item.title?.[0] ?? '',
    authors: formatPeople(item.author),
    year: extractYear(item),
    language,
    language_display,
    abstract: cleanAbstract(item.abstract),
    doi: item.DOI ?? '',
    url: item.URL ?? (item.DOI ? `https://doi.org/${item.DOI}` : ''),
    publisher: item.publisher ?? '',
    pages: item.page ?? '',
    volume: item.volume ?? '',
    issue: item.issue ?? '',
    editors: formatPeople(item.editor),
  };

  if (type === 'article') {
    entry.journal = containerTitle;
  } else if (type === 'chapter') {
    // For a lone chapter this is the best we have; the whole-book importer
    // overrides it with the actual book title.
    entry.booktitle = containerTitle;
  } else if (type === 'conference') {
    entry.conference = containerTitle;
  }

  return { ...entry, ...overrides };
}

async function crossrefGet(path) {
  const sep = path.includes('?') ? '&' : '?';
  const url = `${CROSSREF_BASE}${path}${sep}mailto=${encodeURIComponent(MAILTO)}`;
  let res;
  try {
    res = await fetch(url, { headers: { Accept: 'application/json' } });
  } catch {
    throw new Error('FETCH_FAILED');
  }
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('FETCH_FAILED');
  const json = await res.json();
  return json.message;
}

export async function fetchWork(doi) {
  return crossrefGet(`/works/${encodeURI(doi)}`);
}

// First numeric page in a "143-160" range, for ordering chapters as they appear
// in the book.
function startPage(entry) {
  const m = (entry.pages ?? '').match(/\d+/);
  return m ? parseInt(m[0], 10) : Infinity;
}

// Paste a book link / DOI / ISBN → { book, chapters } where chapters is an array
// of normalized entries in reading order. Throws coded errors the UI maps to
// translated messages: NO_IDENTIFIER, NO_ISBN, NO_CHAPTERS, FETCH_FAILED.
export async function fetchBookChapters(input) {
  const id = parseIdentifier(input);
  if (!id) throw new Error('NO_IDENTIFIER');

  let book = null;
  let isbn = id.kind === 'isbn' ? id.value : null;

  if (id.kind === 'doi') {
    const work = await fetchWork(id.value);
    const isbns = Array.isArray(work?.ISBN) ? work.ISBN : [];
    if (isbns.length) isbn = isbns[0].replace(/[^0-9Xx]/g, '');
    // Only treat the fetched work as the volume record if it actually IS a book.
    // A pasted *chapter* link returns a book-chapter, whose title/editors are
    // not the volume's — don't stamp those onto every chapter.
    if (work && BOOK_TYPES.has(work.type)) book = work;
  }
  if (!isbn) throw new Error('NO_ISBN');

  const list = await crossrefGet(`/works?filter=isbn:${isbn}&rows=300`);
  const allItems = list?.items ?? [];
  const items = allItems.filter((it) => CHAPTER_TYPES.has(it.type));
  if (items.length === 0) throw new Error('NO_CHAPTERS');

  // Recover the volume record when we don't already have it (ISBN paste, or a
  // chapter link): the ISBN query itself usually includes the book as a
  // type:book item.
  if (!book) {
    book = allItems.find((it) => BOOK_TYPES.has(it.type)) ?? null;
  }
  // Last resort: Springer chapter DOIs are "<bookDoi>_<n>" — derive and fetch.
  if (!book) {
    const chapterDoi = items[0]?.DOI ?? '';
    const derived = chapterDoi.replace(/_\d+$/, '');
    if (derived && derived !== chapterDoi) {
      const work = await fetchWork(derived);
      if (work && BOOK_TYPES.has(work.type)) book = work;
    }
  }

  const bookTitle = book?.title?.[0] ?? '';
  const bookEditors = formatPeople(book?.editor);
  const bookPublisher = book?.publisher ?? items[0]?.publisher ?? '';

  const chapters = items
    .map((it) =>
      crossrefToEntry(it, {
        type: 'chapter',
        booktitle: bookTitle || it['container-title']?.[0] || '',
        editors: bookEditors.length ? bookEditors : formatPeople(it.editor),
        publisher: bookPublisher,
      }),
    )
    .sort((a, b) => startPage(a) - startPage(b));

  const bookDoi = book?.DOI ?? '';
  return {
    book: {
      title: bookTitle,
      editors: bookEditors,
      publisher: bookPublisher,
      year: book ? extractYear(book) : chapters[0]?.year ?? null,
      language: book?.language ? String(book.language).toLowerCase() : chapters[0]?.language ?? '',
      isbn,
      doi: bookDoi,
      url: book?.URL ?? (bookDoi ? `https://doi.org/${bookDoi}` : ''),
    },
    chapters,
  };
}

// Paste a single DOI (or a link containing one) → one normalized entry for
// autofilling the manual form. Throws NO_DOI, NOT_FOUND, or FETCH_FAILED.
export async function fetchSingleEntry(input) {
  const id = parseIdentifier(input);
  if (!id || id.kind !== 'doi') throw new Error('NO_DOI');
  const work = await fetchWork(id.value);
  if (!work) throw new Error('NOT_FOUND');
  return crossrefToEntry(work);
}
